/** @file Footprint layer: w x h tiles offset from the root bone (spec 5.10), clipped to the view and capped. */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import { affine } from '@pose2d/math/affine';
import type { Sink } from '../draw/createSink';
import { emitLine } from '../draw/emitLine';
import { emitQuad } from '../draw/emitQuad';
import type { GridSpec } from '../overlayOptions';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { gridLattice } from './gridLattice';
import { pixelsPerUnit } from './pixelsPerUnit';
import { visibleWorldBox } from './visibleWorldBox';

/** A lattice rectangle [uMin, uMax, vMin, vMax] in cells from the root bone. */
type Range = [number, number, number, number];

/** Margin in pixels around the view that still counts as "touching the view" (a line is 1.5 px wide). */
const TOUCH_PX = 2;
const p: [number, number] = [0, 0];
const root: [number, number] = [0, 0];
const quad = new Float64Array(8);
const line = new Float64Array(4);
const worldBox: [number, number, number, number] = [0, 0, 0, 0];

/** Writes the world origin of the root bone (the first bone with parent < 0) into `root`. */
function findRoot(input: OverlayInput): void {
  const o = Math.max(0, input.data.bones.findIndex((b) => b.parent < 0)) * POSE_LAYOUT.WORLD_STRIDE;
  root[0] = input.pose.world[o + 4] ?? NaN;
  root[1] = input.pose.world[o + 5] ?? NaN;
}

/** Pixel position of root + lattice point (u, v), written into `out` at `at`. */
function corner(input: OverlayInput, grid: GridSpec, u: number, v: number, out: Float64Array, at: number): void {
  gridLattice.toWorld(grid, u, v, p);
  affine.apply(input.camera.worldToPixel, p[0] + root[0], p[1] + root[1], p);
  out[at] = p[0];
  out[at + 1] = p[1];
}

/** The viewport grown by `padPx` on every side, as a lattice range from the root; null when not finite. */
function viewRange(sink: Sink, input: OverlayInput, grid: GridSpec, padPx: number): Range | null {
  const pad = padPx / pixelsPerUnit(input.camera);
  const [x0, y0, x1, y1] = visibleWorldBox(sink, worldBox);
  const r = gridLattice.range(grid, [x0 - pad - root[0], y0 - pad - root[1], x1 + pad - root[0], y1 + pad - root[1]]);
  return r.every(Number.isFinite) ? r : null;
}

/** The footprint rectangle in cells from the root, or null for a degenerate (non-finite or empty) footprint. */
function footprintRange(fp: NonNullable<GridSpec['footprint']>): Range | null {
  const r: Range = [fp.offsetX, fp.offsetX + fp.w, fp.offsetY, fp.offsetY + fp.h];
  return fp.w > 0 && fp.h > 0 && r.every(Number.isFinite) ? r : null;
}

/** The range as [along lo, along hi, across lo, across hi] for lines of constant u (axis 0) or constant v (axis 1). */
const alongFirst = (r: Range, axis: 0 | 1): Range => (axis === 0 ? r : [r[2], r[3], r[0], r[1]]);
const touches = (a: Range, b: Range): boolean => a[0] <= b[1] && b[0] <= a[1] && a[2] <= b[3] && b[2] <= a[3];
const clipTo = (a: Range, b: Range): Range => [Math.max(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.min(a[3], b[3])];

/** The footprint as drawn: its size in cells, its rectangle, the lattice near the view and the view itself. */
interface Placement {
  w: number;
  h: number;
  fp: Range;
  near: Range;
  view: Range;
}

/** Interior tile lines a direction may draw besides its two border lines (R7-01). */
const BUDGET = OVERLAY_STYLE.maxGridLines - 2;

/** One tile line of `axis` at index i (0 = start border, `last` = end border), cut to [lo, hi] across. */
function tileLine(sink: Sink, input: OverlayInput, grid: GridSpec, axis: 0 | 1, at: { t: number; lo: number; hi: number; border: boolean }): void {
  corner(input, grid, axis === 0 ? at.t : at.lo, axis === 0 ? at.lo : at.t, line, 0);
  corner(input, grid, axis === 0 ? at.t : at.hi, axis === 0 ? at.hi : at.t, line, 2);
  emitLine(sink, line, at.border ? 1.5 : 1, OVERLAY_STYLE.footprintEdge);
}

/** Interior line indices of the along-range [a0, a1] (footprint start f0, end border `last`) as [first, stop]. */
const interior = (f0: number, a0: number, a1: number, last: number): [number, number] => [Math.max(1, Math.ceil(a0 - f0)), Math.min(last - 1, Math.floor(a1 - f0))];

/** Smallest power-of-two step whose multiples in [first, stop] number at most BUDGET (drawGrid's thinning). */
function thinStep(first: number, stop: number): number {
  let step = 1;
  while (Math.floor(stop / step) - Math.ceil(first / step) + 1 > BUDGET && step < 2 ** 60) step *= 2;
  return step;
}

/**
 * Interior tile lines of one axis as [first index, step, count]: every line near the view (`near`, one viewport of
 * margin) while they fit the budget; otherwise only the lines over the view itself, thinned to every 2nd, 4th, ...
 * line (multiples of the step from the footprint start, so they hold still while panning) until they fit.
 */
function interiorLines(place: Placement, axis: 0 | 1, last: number): [number, number, number] {
  const f0 = alongFirst(place.fp, axis)[0], n = alongFirst(place.near, axis), v = alongFirst(place.view, axis);
  const [first, stop] = interior(f0, n[0], n[1], last);
  if (stop - first + 1 <= BUDGET) return [first, 1, Math.max(0, stop - first + 1)];
  const [vFirst, vStop] = interior(f0, v[0], v[1], last);
  const step = thinStep(vFirst, vStop), j0 = Math.ceil(vFirst / step);
  return [j0 * step, step, Math.max(0, Math.min(BUDGET, Math.floor(vStop / step) - j0 + 1))];
}

/**
 * Tile lines of one axis: constant u (axis 0) or constant v (axis 1) at start + min(i, size) for i = 0..ceil(size).
 * The two border lines (i = 0 and i = last) are always drawn, interior ones as interiorLines picks them, counted by
 * index (a line index past 2^53 no longer advances when incremented). Every line is cut to the footprint part inside
 * `near`, so coordinates stay near the view.
 */
function tileLines(sink: Sink, input: OverlayInput, grid: GridSpec, place: Placement, axis: 0 | 1): void {
  const f = alongFirst(place.fp, axis), n = alongFirst(place.near, axis);
  const size = axis === 0 ? place.w : place.h, last = Math.ceil(size);
  const at = { t: f[0] + Math.min(0, size), lo: Math.max(f[2], n[2]), hi: Math.min(f[3], n[3]), border: true };
  tileLine(sink, input, grid, axis, at);
  const [first, step, count] = interiorLines(place, axis, last);
  at.border = false;
  for (let k = 0, prev = NaN; k < count; k++) {
    const t = f[0] + Math.min(first + k * step, size);
    if (t === prev) continue;
    at.t = prev = t;
    tileLine(sink, input, grid, axis, at);
  }
  at.t = f[0] + Math.min(last, size);
  at.border = true;
  tileLine(sink, input, grid, axis, at);
}

/**
 * `options.grid.footprint`: a translucent fill plus tile lines, following the root bone. Nothing is drawn when the
 * footprint is degenerate or does not touch the view; otherwise geometry is clipped to the view plus one viewport of
 * margin (so a footprint near the screen keeps its exact outline) and tile lines are capped (R7-01).
 */
export function drawFootprint(sink: Sink, input: OverlayInput): void {
  const grid = input.options.grid;
  const fpSpec = grid?.footprint ?? null;
  if (grid === undefined || fpSpec === null) return;
  findRoot(input);
  const fp = footprintRange(fpSpec), seen = viewRange(sink, input, grid, TOUCH_PX), view = viewRange(sink, input, grid, 0);
  const near = viewRange(sink, input, grid, Math.max(sink.width, sink.height));
  if (fp === null || seen === null || near === null || view === null || !touches(fp, seen)) return;
  const [u0, u1, v0, v1] = clipTo(fp, near);
  corner(input, grid, u0, v0, quad, 0);
  corner(input, grid, u1, v0, quad, 2);
  corner(input, grid, u1, v1, quad, 4);
  corner(input, grid, u0, v1, quad, 6);
  emitQuad(sink, quad, OVERLAY_STYLE.footprintFill);
  const place: Placement = { w: fpSpec.w, h: fpSpec.h, fp, near, view };
  tileLines(sink, input, grid, place, 0);
  tileLines(sink, input, grid, place, 1);
}
